import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import 'devextreme-react/text-area'
import type { Editablesede, sede } from '@/model/qsadminapi/QsAdminApiModuleModel'
import {
  ButtonItem,
  ButtonOptions,
  EmailRule,
  EmptyItem,
  Form as DxForm,
  FormRef,
  GroupItem,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form'
import { useFetcher } from 'react-router-dom'
import './ClientSite.scss'
import { IClientEditorItemRef } from '@/routes/clients/client/sites/ClientSitesEditor'
import notify from 'devextreme/ui/notify'
import { PopupRef } from 'devextreme-react/cjs/popup'
import FormPopupClientNote from '@/components/form-popup-client-note/FormPopupClientNote'
import { Roles } from '@/auth/azure/Roles'
import { customButton, isUserRoleAllowed } from '@/routes/utils'
import { AzureUserInfo } from '@/auth/azure/azureManager'
import { ClickEvent } from 'devextreme/ui/button'
import AzureMap from '@/components/azure-map/AzureMap'
import RequeueSiteGeocodingButton from '@/components/site-geocoding/RequeueSiteGeocodingButton'
import type { FieldDataChangedEvent } from 'devextreme/ui/form'

const GEOCODING_FIELDS = ['latitude', 'longitude', 'geocode_status', 'geocode_updated_at', 'geocode_source']

export type SiteEditorProps = {
  site?: sede | Editablesede
  removeSelf: () => void
  onDirty: () => void
  className?: string
  readOnly?: boolean
  writeInfo?: boolean
  userInfo?: AzureUserInfo
  hasUnsavedChanges?: boolean
  onGeocodingQueued: (siteId: number) => void
}

// eslint-disable-next-line react/display-name
export const ClientSite = forwardRef<IClientEditorItemRef, SiteEditorProps>(
  (props: SiteEditorProps, ref: React.ForwardedRef<IClientEditorItemRef>) => {
    const {
      site,
      className,
      readOnly,
      writeInfo,
      userInfo,
      removeSelf,
      onDirty,
      hasUnsavedChanges,
      onGeocodingQueued,
    } = props
    const [fullAddress, setFullAddress] = useState<string>('')
    const fetcher = useFetcher()
    const siteId: number | undefined = site ? ('id' in site ? site?.id : undefined) : undefined
    const [formData, setFormData] = useState(site)
    const [centerMapAddress, setCenterMapAddress] = useState<string>(
      site
        ? [site.strada, site.cap, site.citta, site.provincia, site.stato || 'Italia'].filter(Boolean).join(', ')
        : 'Parma, IT',
    )
    const formRef = useRef<FormRef>(null)
    const popupNoteClienteRef = useRef<PopupRef>(null)
    const [principale, setPrincipale] = useState<boolean | null | undefined>(site?.principale)
    const onFieldDataChanged = useCallback(
      ({ dataField }: FieldDataChangedEvent) => {
        if (dataField && !GEOCODING_FIELDS.includes(dataField)) onDirty()
      },
      [onDirty],
    )
    useEffect(() => {
      const addr = site
        ? [site.strada, site.cap, site.citta, site.provincia, site.stato || 'Italia'].filter(Boolean).join(', ')
        : 'Parma, IT'
      setFullAddress(addr)
      setCenterMapAddress(addr)
    }, [site, setFullAddress, setCenterMapAddress])

    useImperativeHandle(ref, () => {
      return {
        validate() {
          console.log('index item validate', site?.nome)
          return formRef.current?.instance().validate()
        },
        submit() {
          try {
            const payload = { ...site } as Record<string, unknown>
            for (const field of GEOCODING_FIELDS) {
              delete payload[field]
            }
            fetcher.submit(JSON.stringify(payload), {
              method: siteId ? 'PATCH' : 'POST',
              action: `./${siteId ?? 'new'}`,
              encType: 'application/json',
            })
            return true
          } catch (error) {
            console.error(`Error submitting site data ${site?.nome}`, error)
            notify(`Errore salvataggio dati sede ${site?.nome}. Dettagli : ${error}`, 'error', 3000)
            return false
          }
        },
      } satisfies IClientEditorItemRef
    }, [fetcher, site, siteId])

    useEffect(() => {
      setPrincipale(site?.principale)
      setFormData(site)
    }, [site, siteId])

    return (
      <>
        <fetcher.Form className={`${formData?.archived ? 'archived' : ''}`}>
          <DxForm
            className={className}
            id={'azienda_address_form'}
            formData={formData}
            labelLocation={'top'}
            showValidationSummary={true}
            readOnly={readOnly || formData?.archived || false}
            onFieldDataChanged={onFieldDataChanged}
            ref={formRef}
            colCount={1}
          >
            <GroupItem colCount={4}>
              <SimpleItem dataField="nome" colSpan={2} label={{ text: 'Nome Sede' }}>
                <RequiredRule message="Il nome sede è obbligatorio" />
              </SimpleItem>
              <SimpleItem
                dataField="principale"
                colSpan={1}
                label={{ text: 'Principale' }}
                editorType="dxSwitch"
                editorOptions={{
                  readOnly: readOnly || principale || siteId === undefined,
                }}
              />
              {siteId ? (
                <GroupItem colSpan={1} colCount={3}>
                  <EmptyItem></EmptyItem>
                  <ButtonItem cssClass={'form-top-action-button-info'} name="siteInfo">
                    <ButtonOptions
                      disabled={false}
                      icon={'info'}
                      hint="Note Cliente"
                      stylingMode={'text'}
                      onClick={(e) => {
                        popupNoteClienteRef.current?.instance().show()
                      }}
                    />
                  </ButtonItem>
                  <ButtonItem cssClass={'form-top-action-button'} name="siteRestore">
                    <ButtonOptions
                      disabled={readOnly || principale === true}
                      icon={formData?.archived ? 'undo' : 'trash'}
                      stylingMode={'text'}
                      type="success"
                      onClick={async (e) => {
                        // In questo modo il form sente direttamente la modifica
                        if (formData) {
                          setFormData({ ...formData, archived: !formData.archived })
                        }
                        // Fondamentale per modificare anche la data source
                        if (site) {
                          site.archived = !site.archived
                        }
                        // In questo caso forzo il dirty sul controllo
                        onDirty()
                      }}
                    />
                  </ButtonItem>
                </GroupItem>
              ) : (
                <ButtonItem cssClass={'form-top-action-button'} name="siteDelete">
                  <ButtonOptions
                    disabled={principale ?? false}
                    icon={'trash'}
                    stylingMode={'text'}
                    type="danger"
                    onClick={(e) => {
                      removeSelf()
                    }}
                  />
                </ButtonItem>
              )}
            </GroupItem>
            <GroupItem caption="Indirizzo" colCount={5}>
              <GroupItem colCount={5} colSpan={5}>
                <SimpleItem dataField="strada" colSpan={2} label={{ text: 'Indirizzo' }}>
                  <RequiredRule message="L'indirizzo è una informazione obbligatoria" />
                </SimpleItem>
                <SimpleItem dataField="citta" colSpan={2} label={{ text: 'Citta' }}>
                  <RequiredRule message="Città è un campo obbligatorio" />
                </SimpleItem>
                <SimpleItem dataField="provincia" colSpan={1} label={{ text: 'Provincia' }} />
                <SimpleItem dataField="cap" label={{ text: 'CAP' }} />
                <SimpleItem dataField="stato" label={{ text: 'Stato' }} />
                <SimpleItem dataField="latitude" label={{ text: 'Latitudine' }} editorOptions={{ readOnly: true }} />
                <SimpleItem dataField="longitude" label={{ text: 'Longitudine' }} editorOptions={{ readOnly: true }} />
                {siteId &&
                  !formData?.archived &&
                  isUserRoleAllowed(userInfo?.roles, [
                    Roles.GlobalAdministrator,
                    Roles.Administrator,
                    Roles.Supervisor,
                  ]) && (
                    <SimpleItem name="requeueGeocoding" cssClass="site-geocoding-action" label={{ visible: false }}>
                      <RequeueSiteGeocodingButton
                        key={`${siteId}-${site?.geocode_status}-${site?.geocode_updated_at}`}
                        iconOnly
                        siteId={siteId}
                        canRequeue={!!site?.geocode_status && site.geocode_status !== 'pending'}
                        hasCoordinates={site?.latitude != null || site?.longitude != null}
                        hasUnsavedChanges={hasUnsavedChanges}
                        onQueued={onGeocodingQueued}
                      />
                    </SimpleItem>
                  )}
              </GroupItem>
              <SimpleItem colSpan={5}>
                <AzureMap
                  id={siteId?.toString() ?? 'unidentified'}
                  placeName={centerMapAddress}
                  height={'30vh'}
                  latitude={site?.latitude}
                  longitude={site?.longitude}
                />
              </SimpleItem>
            </GroupItem>
            <GroupItem caption="Contatti" colCount={4}>
              <SimpleItem
                dataField="telefono"
                colSpan={1}
                label={{ text: 'Telefono' }}
                editorOptions={{
                  buttons: [
                    customButton('call', (e: ClickEvent) => {
                      if (formData?.telefono) window.open(`tel:${formData?.telefono.replace(/\s+/g, '')}`, '_self')
                      else notify('Nessun numero di telefono da chiamare.', 'warning', 2000)
                    }),
                  ],
                }}
              >
                <RequiredRule message="Il numero di telefono è una informazione obbligatoria" />
              </SimpleItem>
              <GroupItem colSpan={3} colCount={2}>
                <SimpleItem colSpan={1} dataField="email" label={{ text: 'Email' }}>
                  <EmailRule message="L'indirizzo email non è valido" ignoreEmptyValue={true} />
                  <RequiredRule message="L'indirizzo email è una informazione obbligatoria" />
                </SimpleItem>
                <SimpleItem colSpan={1} dataField="email_pec" label={{ text: 'PEC' }}>
                  <EmailRule message="L'indirizzo email non è valido" ignoreEmptyValue={true} />
                </SimpleItem>
              </GroupItem>
            </GroupItem>
          </DxForm>
        </fetcher.Form>
        <FormPopupClientNote
          data={site}
          refPopup={popupNoteClienteRef}
          isRoleAllowed={isUserRoleAllowed(userInfo?.roles, [
            Roles.TechDeveloper,
            Roles.Sales,
            Roles.GlobalAdministrator,
            Roles.ExternalSales,
            Roles.Administrator,
          ])}
        ></FormPopupClientNote>
      </>
    )
  },
)
